import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Input, InputGroup, ButtonGroup, Button } from 'rsuite';
import moment from 'moment';
import { SharedApi } from '../../../services/Shared.api';
import {DeviceLogsProps, LIMITS, Log} from "./DeviceLogs.model";
import UpdateButton from "../../shared/UpdateButton/UpdateButton";

const { Column, HeaderCell, Cell } = Table;

const DeviceLogs = ({ deviceId }: DeviceLogsProps) => {
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(false);

  const [sortColumn, setSortColumn] = useState<string | null>('insertDateTime');
  const [sortType, setSortType] = useState<'asc' | 'desc' | null>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredLogs, setFilteredLogs] = useState<Log[]>([]);

  const [limit, setLimit] = useState<number>(50);

  useEffect(() => {
    getLogs().then();
  }, [deviceId]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredLogs(logs);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = logs.filter((log) =>
      log.data?.toLowerCase().includes(searchLower) ||
      moment(log.insertDateTime)
        .format('DD.MM.YYYY HH:mm:ss')
        .toLowerCase()
        .includes(searchLower)
    );

    setFilteredLogs(filtered);
  }, [logs, searchTerm]);

  const getLogs = async () => {
    setLoading(true);
    try {
      const response = await SharedApi.getDeviceLogs(deviceId);
      if (response?.logs) {
        setLogs(response.logs);
        setFilteredLogs(response.logs);
      } else {
        setLogs([]);
        setFilteredLogs([]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getSortedData = useCallback(() => {
    const data = [...filteredLogs];

    if (sortColumn && sortType) {
      data.sort((a, b) => {
        let valueA: number | string = a[sortColumn as keyof Log] as any;
        let valueB: number | string = b[sortColumn as keyof Log] as any;

        if (sortColumn === 'insertDateTime') {
          const timeA = new Date(a.insertDateTime).getTime();
          const timeB = new Date(b.insertDateTime).getTime();
          valueA = timeA;
          valueB = timeB;
        }

        if (valueA < valueB) return sortType === 'asc' ? -1 : 1;
        if (valueA > valueB) return sortType === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return data.slice(0, limit);
  }, [filteredLogs, sortColumn, sortType, limit]);

  const handleSortColumn = (column: string, type: 'asc' | 'desc') => {
    setSortColumn(column);
    setSortType(type);
  };


  const handleClearSearch = () => setSearchTerm('');

  const handleChangeLimit = (newLimit: number) => {
    setLimit(newLimit);
  };

  return (
    <Card style={{ padding: '1rem' }}>
      <div className="search-panel mb-3">
        <div className="row align-items-center">
          <div className="col-md-6 col-lg-4">
            <InputGroup>
              <Input
                placeholder="Поиск по логам..."
                value={searchTerm}
                onChange={setSearchTerm}
              />
              {searchTerm && (
                <InputGroup.Button onClick={handleClearSearch}>
                  ×
                </InputGroup.Button>
              )}
            </InputGroup>
          </div>

          <div className="col-md-5 col-lg-3 mt-2 mt-md-0">
            <ButtonGroup>
              {LIMITS.map((l) => (
                <Button
                  key={l}
                  size="sm"
                  appearance={limit === l ? 'primary' : 'ghost'}
                  onClick={() => handleChangeLimit(l)}
                >
                  {l}
                </Button>
              ))}
            </ButtonGroup>
          </div>

          <div className="col-lg-4 text-md-end mt-2 mt-lg-0">
            <span className="text-muted">
              Показано: {Math.min(limit, filteredLogs.length)} из {filteredLogs.length} (всего {logs.length})
            </span>
          </div>
          <div className="col-lg-1 text-md-end mt-2 mt-lg-0">
            <UpdateButton loading={loading} onRefresh={getLogs}/>
          </div>
        </div>
      </div>

      <Table
        autoHeight
        data={getSortedData()}
        sortColumn={sortColumn || undefined}
        sortType={sortType || undefined}
        onSortColumn={handleSortColumn as any}
        loading={loading}
        rowKey="id"
        wordWrap
      >
        <Column width={220} sortable fixed>
          <HeaderCell>Дата и время</HeaderCell>
          <Cell dataKey="insertDateTime">
            {(rowData: Log) =>
              moment(rowData.insertDateTime).format('DD.MM.YYYY HH:mm:ss')
            }
          </Cell>
        </Column>

        <Column flexGrow={1}>
          <HeaderCell>Сообщение</HeaderCell>
          <Cell dataKey="data">
            {(rowData: Log) => (
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  margin: 0
                }}
              >
          {rowData.data}
        </pre>
            )}
          </Cell>
        </Column>
      </Table>


    </Card>
  );
};

export default DeviceLogs;
